import moment from 'moment'
import util from './util'

// The two predicates behind the patient list: "is this patient scheduled?" and
// "does this patient match the quick search?".
//
// 🔴 THEY LIVE HERE BECAUSE THE PAGE ONCE CONTRADICTED ITSELF. The PATIENTS
// header counted every active patient while the table applied the Surgery Date
// filter, so on 2026-10-28 a clinic saw "Active: 2 | KNEE: 2" above an EMPTY
// table -- both numbers correct, describing different populations, with
// nothing on screen to say so. Two patients had registered that morning and
// looked lost.
//
// Adding per-tab counts in a third component would have repeated exactly that
// mistake. One definition, imported by everything that counts or filters
// patients, is what stops the list and the numbers above it drifting apart.

// Surgery dates this far out or further are treated as "not scheduled".
//
// Not arbitrary: patient self-registration offers "Surgery date not known" and
// transmits TODAY + 1 YEAR when it is chosen (physio_app_web
// components/login/registration.js:69,227). Six months sits clear of a real
// pre-op booking and clear of that placeholder, so the bucket means "we do not
// have a real date for this patient" rather than "the operation is far off".
export const SCHEDULE_THRESHOLD_MONTHS = 6

// A patient with NO surgery date at all counts as scheduled, deliberately: it
// keeps them in the default view rather than hiding them behind a tab nobody
// opens. Absent data should not make a patient disappear.
export const isScheduledPatient = (patient) => {
  if (!patient) return true
  if (!patient.surgery?.date) return true
  return moment(patient.surgery.date).isBefore(
    moment().add(SCHEDULE_THRESHOLD_MONTHS, 'months')
  )
}

// Does this patient belong in the given Surgery Date view?
// `inactive` ignores the view entirely -- the toggle is hidden there.
export const matchesScheduleView = (patient, scheduleView, filters) => {
  if (filters?.type === 'inactive') return true
  if (!scheduleView || scheduleView === 'all') return true
  const scheduled = isScheduledPatient(patient)
  return scheduleView === 'unscheduled' ? !scheduled : scheduled
}

// Client-side quick search across first/last name (prefix), full name, surgery
// type, provider, surgery date, and post-/pre-op day count.
//
// `surgeons` is the loaded surgeon list, needed to resolve a patient's surgeon
// id to a name. Pass it or a search by provider name silently matches nothing.
export const matchesPatientSearch = (patient, term, surgeons) => {
  if (!term) return true
  const t = String(term).trim().toLowerCase()
  if (!t) return true
  const p = patient || {}
  const first = (p.firstname || '').toLowerCase()
  const last = (p.lastname || '').toLowerCase()
  const fullName = (first + ' ' + last).trim()
  const type = (p.surgery?.type || '').toLowerCase()
  const surgeonName = (
    util.getObj(surgeons, p.surgeon)?.name || ''
  ).toLowerCase()
  const providerName = (
    (p.provider?.firstname || '') + ' ' + (p.provider?.lastname || '')
  )
    .trim()
    .toLowerCase()
  const date = p.surgery?.date
    ? moment(p.surgery.date).format('MM/DD/YYYY')
    : ''
  let dayMatch = false
  if (p.surgery?.date) {
    const diff = moment()
      .startOf('day')
      .diff(moment(p.surgery.date).startOf('day'), 'days')
    const count = Math.abs(diff)
    const label = `${diff >= 0 ? 'post-op' : 'pre-op'} ${count} days`
    dayMatch = String(count) === t || label.includes(t)
  }
  return (
    first.startsWith(t) ||
    last.startsWith(t) ||
    fullName.startsWith(t) ||
    type.includes(t) ||
    surgeonName.includes(t) ||
    providerName.includes(t) ||
    date.includes(t) ||
    dayMatch
  )
}

// How many patients each Surgery Date tab would show, for the CURRENT search.
//
// Search-aware on purpose. A tab reading "Scheduled (32)" above a table
// showing three rows because a search is active would be the same
// count-disagrees-with-list problem this module exists to prevent.
export const scheduleCounts = (patients, term, surgeons) => {
  const list = (patients || []).filter((p) =>
    matchesPatientSearch(p, term, surgeons)
  )
  let scheduled = 0
  list.forEach((p) => {
    if (isScheduledPatient(p)) scheduled += 1
  })
  return {
    scheduled,
    unscheduled: list.length - scheduled,
    all: list.length,
  }
}

export default {
  SCHEDULE_THRESHOLD_MONTHS,
  isScheduledPatient,
  matchesScheduleView,
  matchesPatientSearch,
  scheduleCounts,
}
